import type { ConnectionInterface } from 'store/connections/types';
import type { UnitInterface } from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import partition from 'util/partition';

type Node = {
  key: string;
  neighbours: string[];
};

type Graph = {
  [key: string]: Node;
};

export const getFlowOrder = (
  units: UnitInterface[],
  connections: ConnectionInterface[],
): string[][] => {
  const graph = connections.reduce((acc: Graph, curr) => {
    return {
      ...acc,
      [curr.from]: {
        key: curr.from,
        neighbours: acc[curr.from]?.neighbours
          ? [...(acc[curr.from]?.neighbours || []), curr.to]
          : [curr.to],
      },
    };
  }, {});

  const startUnit = units.find((u) => u.type === UnitTypes.FEED);

  if (!startUnit) {
    return [];
  }

  const initialPath = [startUnit.key];

  const result: string[][] = [];
  const findAllPaths = (path: string[]) => {
    const currentNodeKey = path[path.length - 1];
    const currentNode = graph[currentNodeKey];
    if (!currentNode?.neighbours) {
      result.push(path);
      return;
    }

    for (const neighbour of currentNode.neighbours) {
      if (!path.includes(neighbour)) {
        findAllPaths([...path, neighbour]);
      }
    }
  };

  findAllPaths(initialPath);

  return result;
};

export const sortByFlowOrder = (
  units: UnitInterface[],
  connections: ConnectionInterface[],
) => {
  const flowOrder = getFlowOrder(units, connections);
  const length = flowOrder.reduce(
    (acc, curr) => (curr.length > acc ? curr.length : acc),
    0,
  );

  const unitOrder: string[] = [];
  for (let i = 0; i < length; i++) {
    flowOrder.forEach((path) => {
      if (path[i] && !unitOrder.includes(path[i])) {
        unitOrder.push(path[i]);
      }
    });
  }

  let sortedUnits: UnitInterface[] = [];

  const [connectedUnits, unconnected] = partition(
    units,
    (u) => unitOrder.indexOf(u.key) !== -1,
  );

  sortedUnits = sortedUnits.concat(
    connectedUnits.sort((a, b) => {
      return unitOrder.indexOf(a.key) - unitOrder.indexOf(b.key);
    }),
  );

  // Units that are not connected also need to be sorted to guarantee a stable sort order
  sortedUnits = sortedUnits.concat(
    unconnected.sort((a, b) => a.key.localeCompare(b.key)),
  );

  return sortedUnits;
};
